// pages/Blog.js
import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import "../styles/Blog.css";

const CATEGORIES = [
  "All",
  "Recruitment",
  "Culture",
  "Compliance",
  "Performance",
  "Well-being",
  "DEI",
];

const categoryColors = {
  Recruitment: { bg: "#EEF2FF", text: "#4338CA", border: "#C7D2FE" },
  Culture: { bg: "#F0FDF4", text: "#166534", border: "#BBF7D0" },
  Compliance: { bg: "#FFF7ED", text: "#9A3412", border: "#FED7AA" },
  Performance: { bg: "#EFF6FF", text: "#1D4ED8", border: "#BFDBFE" },
  "Well-being": { bg: "#FDF4FF", text: "#7E22CE", border: "#E9D5FF" },
  DEI: { bg: "#FFF1F2", text: "#9F1239", border: "#FECDD3" },
};

const API_BASE_URL = "https://jobs.mpdatahub.com/api";

export default function Blog() {
  const navigate = useNavigate();
  const [blogs, setBlogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [activeCategory, setActiveCategory] = useState("All");
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedBlog, setSelectedBlog] = useState(null);
  const [toast, setToast] = useState(null);

  // Fetch blogs from API
  useEffect(() => {
    fetchBlogs();
  }, []);

  useEffect(() => {
    if (toast) {
      const timer = setTimeout(() => setToast(null), 3000);
      return () => clearTimeout(timer);
    }
  }, [toast]);

  const fetchBlogs = async () => {
    try {
      setLoading(true);
      const response = await fetch(`${API_BASE_URL}/blogs`);
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      const data = await response.json();

      // Transform the data to match the expected format
      const transformedData = data.map((blog) => ({
        id: blog.id,
        title: blog.title,
        excerpt: blog.excerpt,
        content: blog.content,
        category: blog.category,
        author: blog.author || "Anonymous",
        authorRole: blog.authorRole || "Contributor",
        tags: blog.tags || [],
        featured: blog.featured,
        readTime: blog.readTime,
        date: blog.date,
        image: blog.image_url, // Map image_url to image
        slug: blog.slug,
      }));

      setBlogs(transformedData);
      setError(null);
    } catch (err) {
      console.error("Error fetching blogs:", err);
      setError("Failed to load blogs. Please try again later.");
    } finally {
      setLoading(false);
    }
  };

  const showToastMessage = (msg, type = "success") => setToast({ msg, type });

  const handleDeleteBlog = async (id) => {
    if (window.confirm("Are you sure you want to delete this blog?")) {
      try {
        const response = await fetch(`${API_BASE_URL}/blogs/${id}`, {
          method: "DELETE",
        });
        if (!response.ok) throw new Error("Failed to delete");
        fetchBlogs();
        if (selectedBlog?.id === id) setSelectedBlog(null);
        showToastMessage("Blog deleted successfully");
      } catch (err) {
        console.error("Error deleting blog:", err);
        showToastMessage("Failed to delete blog", "error");
      }
    }
  };

  const filteredBlogs = blogs.filter((b) => {
    const matchCat = activeCategory === "All" || b.category === activeCategory;
    const matchSearch =
      !searchQuery ||
      b.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
      b.excerpt.toLowerCase().includes(searchQuery.toLowerCase()) ||
      b.tags?.some((t) => t.toLowerCase().includes(searchQuery.toLowerCase()));
    return matchCat && matchSearch;
  });

  const featuredBlog = blogs.find((b) => b.featured);
  const regularBlogs = filteredBlogs.filter(
    (b) => !b.featured || activeCategory !== "All" || searchQuery,
  );

  const BlogCard = ({ blog, large = false }) => (
    <div
      className={`blog-card ${large ? "blog-card-large" : ""}`}
      onClick={() => setSelectedBlog(blog)}
    >
      <div className="blog-card-image-wrapper">
        <img src={blog.image} alt={blog.title} className="blog-card-image" />
        <span
          className="blog-category-badge"
          style={{
            background: categoryColors[blog.category]?.bg || "#F3F4F6",
            color: categoryColors[blog.category]?.text || "#374151",
          }}
        >
          {blog.category}
        </span>
        {blog.featured && (
          <span className="blog-featured-badge">⭐ Featured</span>
        )}

        {/* Admin actions */}
        <div
          className="blog-admin-actions"
          onClick={(e) => e.stopPropagation()}
        >
          <button
            className="admin-action edit"
            onClick={() => navigate(`/blog/edit/${blog.id}`)}
          >
            ✏️
          </button>
          <button
            className="admin-action delete"
            onClick={() => handleDeleteBlog(blog.id)}
          >
            🗑️
          </button>
        </div>
      </div>
      <div className="blog-card-content">
        <h3 className="blog-card-title">{blog.title}</h3>
        <p className="blog-card-excerpt">{blog.excerpt}</p>
        <div className="blog-card-meta">
          <div className="blog-author">
            <div className="author-avatar">
              {blog.author
                ?.split(" ")
                .map((n) => n[0])
                .join("")
                .slice(0, 2) || "AN"}
            </div>
            <div>
              <div className="author-name">{blog.author || "Anonymous"}</div>
              <div className="author-role">
                {blog.authorRole || "Contributor"}
              </div>
            </div>
          </div>
          <div className="blog-date">
            <span>{blog.date}</span>
            <span>·</span>
            <span>{blog.readTime}</span>
          </div>
        </div>
      </div>
    </div>
  );

  const BlogDetailView = ({ blog }) => (
    <div className="blog-detail-view">
      <button className="back-button" onClick={() => setSelectedBlog(null)}>
        ← Back to all blogs
      </button>
      <div className="blog-detail-hero">
        <img src={blog.image} alt={blog.title} className="blog-detail-image" />
        <div className="blog-detail-overlay">
          <span
            className="blog-category-badge large"
            style={{
              background: categoryColors[blog.category]?.bg,
              color: categoryColors[blog.category]?.text,
            }}
          >
            {blog.category}
          </span>
          <h1 className="blog-detail-title">{blog.title}</h1>
          <div className="blog-detail-meta">
            <div className="author-avatar large">
              {blog.author
                ?.split(" ")
                .map((n) => n[0])
                .join("")
                .slice(0, 2) || "AN"}
            </div>
            <div>
              <div className="author-name">{blog.author || "Anonymous"}</div>
              <div className="author-role">
                {blog.authorRole || "Contributor"}
              </div>
            </div>
            <div className="blog-detail-date">
              <div>{blog.date}</div>
              <div>{blog.readTime}</div>
            </div>
          </div>
        </div>
      </div>
      <div className="blog-detail-content">
        {blog.content?.split("\n\n").map((para, i) => (
          <p key={i} className="blog-detail-paragraph">
            {para}
          </p>
        ))}
        <div className="blog-detail-tags">
          {blog.tags?.map((tag) => (
            <span key={tag} className="tag">
              #{tag}
            </span>
          ))}
        </div>
      </div>
      <div className="blog-detail-admin">
        <button
          className="btn-secondary"
          onClick={() => navigate(`/blog/edit/${blog.id}`)}
        >
          ✏️ Edit Article
        </button>
        <button
          className="btn-danger"
          onClick={() => handleDeleteBlog(blog.id)}
        >
          🗑️ Delete Article
        </button>
      </div>
    </div>
  );

  if (loading) {
    return (
      <div className="hr-blog-app">
        <div className="loading-container">
          <div className="loading-spinner"></div>
          <p>Loading amazing content...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="hr-blog-app">
        <div className="error-container">
          <div className="error-icon">⚠️</div>
          <h2>Something went wrong</h2>
          <p>{error}</p>
          <button className="retry-button" onClick={fetchBlogs}>
            Try Again
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="hr-blog-app">
      {/* MAIN CONTENT */}
      {selectedBlog ? (
        <BlogDetailView blog={selectedBlog} />
      ) : (
        <>
          {/* Search and Categories */}
          <div className="search-section">
            <div className="search-container">
              <input
                type="text"
                className="search-input"
                placeholder="🔍 Search articles by title, content, or tags..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
              />
            </div>

            <div className="categories-section">
              {CATEGORIES.map((cat) => (
                <button
                  key={cat}
                  className={`category-btn ${activeCategory === cat ? "active" : ""}`}
                  onClick={() => setActiveCategory(cat)}
                >
                  {cat}
                </button>
              ))}
            </div>
          </div>

          <div className="main-content">
            {/* Featured Article */}
            {featuredBlog && activeCategory === "All" && !searchQuery && (
              <div className="featured-section">
                <div className="section-header">
                  <span className="section-icon">⭐</span>
                  <h2 className="section-title">Featured Article</h2>
                </div>
                <BlogCard blog={featuredBlog} large />
              </div>
            )}

            {/* All Articles */}
            <div className="articles-section">
              <div className="section-header">
                <span className="section-icon">📚</span>
                <h2 className="section-title">
                  {searchQuery
                    ? `Search Results (${filteredBlogs.length})`
                    : activeCategory === "All"
                      ? "Latest Articles"
                      : activeCategory}
                </h2>
              </div>

              <div className="blogs-grid">
                {filteredBlogs.length === 0 ? (
                  <div className="empty-state">
                    <p>📭 No articles found.</p>
                    <button
                      className="create-first-btn"
                      onClick={() => navigate("/blog/new")}
                    >
                      Create your first article
                    </button>
                  </div>
                ) : (
                  (activeCategory === "All" && !searchQuery
                    ? regularBlogs
                    : filteredBlogs
                  ).map((blog) => <BlogCard key={blog.id} blog={blog} />)
                )}
              </div>
            </div>
          </div>
        </>
      )}

      {/* TOAST NOTIFICATION */}
      {toast && (
        <div
          className={`toast-notification ${toast.type === "error" ? "error" : "success"}`}
        >
          {toast.msg}
        </div>
      )}
    </div>
  );
}
